/**
 * Extract Cloudflare UUID from image URL regardless of domain/format.
 * Supports both imagedelivery.net and cribcritic.ai formats.
 * 
 * Examples:
 * - https://cribcritic.ai/images/openai/f7ae8aa9-454d-4b2a-53f3-bccf3a8e0300.jpg
 * - https://imagedelivery.net/hash/f7ae8aa9-454d-4b2a-53f3-bccf3a8e0300/public
 * 
 * @param url Image URL to extract UUID from
 * @returns UUID string or null if not found
 */
export function extractImageUuid(url: string): string | null {
  try {
    // Pattern to match UUID-like strings (8-4-4-4-12 characters)
    const uuidPattern = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
    const match = url.match(uuidPattern);
    return match ? match[0] : null;
  } catch (error) {
    console.warn('Failed to extract UUID from image URL:', url, error);
    return null;
  }
}

/**
 * Find attachment by URL using UUID-based matching to handle different URL formats.
 * First tries exact URL match, then falls back to UUID-based matching.
 * 
 * @param attachments List of attachments to search
 * @param imageUrl URL to match against
 * @returns Matching attachment or undefined
 */
export function findAttachmentByUrl<T extends { key: string; image_url?: string }>(
  attachments: T[], 
  imageUrl: string | undefined
): T | undefined {
  if (!imageUrl) return undefined;
  
  // First try exact URL match
  const exactMatch = attachments.find(attachment => attachment.image_url === imageUrl);
  if (exactMatch) return exactMatch;
  
  // If no exact match, try UUID-based matching
  const targetUuid = extractImageUuid(imageUrl);
  if (targetUuid) {
    return attachments.find(attachment => {
      if (!attachment.image_url) return false;
      return extractImageUuid(attachment.image_url) === targetUuid;
    });
  }
  
  return undefined;
}